/** Locale resolution: cookie wins, then Accept-Language, then English. */
export const LOCALES = ["en", "ru", "id"] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * What the picker calls each language: its own name, in itself. The phone's
 * `AppLanguage.displayName`, and its reason — "each display name stays in
 * its own language so someone who accidentally switches to an unfamiliar
 * one can still find the way back by sight". Constants, not strings to
 * translate: a name in its own language is the same in every other.
 */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  ru: "Русский",
  id: "Bahasa Indonesia",
};

export const LOCALE_COOKIE = "sf-lang";

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function pickLocale(cookieHeader: string | null, acceptLanguage: string | null): Locale {
  const cookie = cookieHeader?.match(/(?:^|;\s*)sf-lang=([a-zA-Z-]+)(?:;|$)/)?.[1]?.toLowerCase();
  if (cookie && isLocale(cookie)) return cookie;

  // "id-ID,id;q=0.9,en;q=0.8" → primary subtags in descending q-order.
  const candidates = (acceptLanguage ?? "")
    .split(",")
    .map((part) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = Number(params.find((p) => p.trim().startsWith("q="))?.split("=")[1] ?? "1");
      return { primary: tag.trim().toLowerCase().split("-")[0] ?? "", q: Number.isNaN(q) ? 0 : q };
    })
    .filter((c) => c.primary.length > 0)
    .sort((a, b) => b.q - a.q);

  for (const candidate of candidates) {
    if (isLocale(candidate.primary)) return candidate.primary;
  }
  return "en";
}
